/**
 * On-demand image lookup — driven by the `apps/images` micro-service.
 *
 * URL shape: <VITE_IMAGE_URL>/v1/image?q=<encoded>
 *
 * The service searches Pexels (or returns from cache) for the gloss and
 * serves the bytes. Failure modes the UI must handle:
 *
 *   - 404: legitimate "no image for this query" (negative-cached). The
 *     `<img>` `onerror` handler in <CardImage> swaps in the "no image"
 *     placeholder. Same code path covers network failures and 5xx.
 *   - 429 / 502: same — image is non-essential, never block the card.
 *
 * Dev: `VITE_IMAGE_URL` is unset → empty base means we fall through to
 * the Vite proxy (added in vite.config.ts) which forwards `/v1/image` to
 * the local `apps/images` service. Prod: this is the absolute URL of
 * the service (e.g. `https://images.rmnlng.com`).
 *
 * The query string itself is the cache key on the server, so two cards
 * sharing the same gloss share the same URL → free browser cache hit.
 */

import { useQuery } from "@tanstack/react-query";

const IMAGE_BASE = import.meta.env.VITE_IMAGE_URL ?? "";

export function imageUrl(query: string): string {
  return `${IMAGE_BASE}/v1/image?q=${encodeURIComponent(query)}`;
}

export function imageMetaUrl(query: string): string {
  return `${IMAGE_BASE}/v1/image-meta?q=${encodeURIComponent(query)}`;
}

/**
 * Photographer / source attribution for an on-demand image. Mirrors
 * the sidecar JSON the image service serves at `/v1/image-meta`.
 * Pexels' guidance is "appreciated, not necessary" — we surface it
 * anyway because crediting the photographer is the right thing to do.
 */
export type ImageMeta = {
  photographer: string;
  photographer_url: string;
  source_url: string;
  alt: string | null;
};

/**
 * Fetch the photographer credit for an on-demand image. Returns `null`
 * (not error) on 404 — that's a legitimate "no metadata" answer
 * (negative-cached query, or a legacy image cached before sidecars
 * shipped). Other failures (network, 5xx) propagate as errors and the
 * caller falls back to no-attribution rendering.
 *
 * Disabled when `query` is null — used by callers that conditionally
 * have a query (e.g. cards with a curator-set imageUrl, where
 * imageQuery is null and there's nothing to look up).
 *
 * `staleTime: Infinity` because the metadata is content-addressed by
 * hash on the server: the same query always returns the same bytes,
 * forever. No need to ever refetch within a session.
 */
export function useImageMeta(query: string | null) {
  return useQuery<ImageMeta | null>({
    queryKey: ["imageMeta", query],
    queryFn: async () => {
      if (!query) return null;
      const res = await fetch(imageMetaUrl(query));
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`image-meta ${res.status}`);
      return (await res.json()) as ImageMeta;
    },
    enabled: !!query,
    staleTime: Infinity,
    // Attribution is purely cosmetic — don't burn re-renders on
    // background refetches when window regains focus.
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });
}
